import { AchFrequencyModelDto, FrequencyModelDto } from '@treasury/api/channel';
import { format } from 'date-fns';
import {
    type Frequency,
    FrequencyDay,
    type FrequencyRange,
    FrequencyType,
    type MonthlyFrequency,
    type TwiceMonthlyFrequency,
    type WeeklyFrequency,
} from './tm-frequency.types';

/**
 *
 * @param frequencyDto
 * @param type
 * @returns
 * @issues
 *  startOn is allowed to be undefined and not handled
 */
const getStartDate = (
    frequencyDto: FrequencyModelDto | AchFrequencyModelDto,
    type: FrequencyType
): Date => {
    if (type === FrequencyType.OneTime) {
        if ('valueDate' in frequencyDto) {
            return new Date(frequencyDto.valueDate as string);
        }
        if ('effectiveDate' in frequencyDto) {
            return new Date(frequencyDto.effectiveDate as string);
        }
    }

    return new Date(frequencyDto.startOn as string);
};

/**
 *
 * @param frequencyDto
 * @returns Frequency
 * @issues
 *  Frequency.repeatOnLastBusinessDay is typed as boolean but is unset in certain instances
 *  Frequency.repeatOn is typed as [number, number|null], the second date should be null if it hasn't been defined, this isn't happening
 *  Frequency.repeatOn is allowed to be undefined but not typed for that
 */
export const mapFrequencyModelDtoToFrequency = (
    frequencyDto: FrequencyModelDto | AchFrequencyModelDto
): Frequency => {
    const type = frequencyDto.type.replace(/\s/g, '') as FrequencyType;
    const startDate = getStartDate(frequencyDto, type);
    const { endOn, noEndDate, repeatOn, repeatOnDay1, repeatOnDay2, repeatOnLastBusinessDay } =
        frequencyDto;

    switch (type) {
        case FrequencyType.OneTime: {
            return { type, startDate };
        }
        case FrequencyType.Weekly: {
            return {
                type,
                startDate,
                endDate: endOn ? new Date(frequencyDto.endOn as string) : undefined,
                perpetual: noEndDate || undefined,
                repeatOn: repeatOn
                    ? FrequencyDay[repeatOn as keyof typeof FrequencyDay]
                    : (repeatOnDay1 ?? FrequencyDay.Monday),
            };
        }
        case FrequencyType.EveryTwoWeeks: {
            return {
                type,
                startDate,
                endDate: endOn ? new Date(frequencyDto.endOn as string) : undefined,
                perpetual: noEndDate || undefined,
                repeatOn: repeatOn
                    ? FrequencyDay[repeatOn as keyof typeof FrequencyDay]
                    : (repeatOnDay1 ?? FrequencyDay.Monday),
            };
        }
        case FrequencyType.Monthly: {
            const getRepeatOn = () => {
                if (!repeatOnDay1) {
                    return undefined;
                }
                return repeatOnDay1;
            };
            return {
                type,
                startDate,
                endDate: endOn ? new Date(frequencyDto.endOn as string) : undefined,
                perpetual: noEndDate || undefined,
                repeatOn: getRepeatOn(),
                repeatOnLastBusinessDay: repeatOnLastBusinessDay || undefined,
            } as MonthlyFrequency;
        }
        case FrequencyType.TwiceAMonth: {
            const getRepeatOn = () => {
                if (!repeatOnDay1) {
                    return undefined;
                }
                return [repeatOnDay1, repeatOnDay2];
            };
            return {
                type,
                startDate,
                endDate: endOn ? new Date(frequencyDto.endOn as string) : undefined,
                perpetual: noEndDate || undefined,
                repeatOn: getRepeatOn(),
                repeatOnLastBusinessDay: repeatOnLastBusinessDay || undefined,
            } as TwiceMonthlyFrequency;
        }
        case FrequencyType.EverySixMonths:
        case FrequencyType.Quarterly:
        case FrequencyType.Yearly: {
            return {
                type,
                startDate: startDate,
                endDate: endOn ? new Date(frequencyDto.endOn as string) : undefined,
                perpetual: noEndDate || undefined,
            } as FrequencyRange;
        }
        default:
            throw new Error(`Case for FrequencyType - ${type} not handled.`);
    }
};

export const mapFrequencyToFrequencyModelDto = (
    frequency: Frequency,
    ach = false
): FrequencyModelDto | AchFrequencyModelDto => {
    const { type } = frequency;
    switch (type) {
        case FrequencyType.OneTime: {
            if (ach) {
                return {
                    type,
                    effectiveDate: format(frequency.startDate, 'MM/dd/yyyy'),
                } as AchFrequencyModelDto;
            }
            return {
                type,
                valueDate: format(frequency.startDate, 'MM/dd/yyyy'),
            } as FrequencyModelDto;
        }
        case FrequencyType.EverySixMonths:
        case FrequencyType.Quarterly:
        case FrequencyType.Yearly: {
            const { endDate, perpetual, startDate } = frequency;
            return {
                type,
                startOn: format(startDate, 'MM/dd/yyyy'),
                endOn: endDate ? format(endDate, 'MM/dd/yyyy') : undefined,
                noEndDate: perpetual,
            } as FrequencyModelDto;
        }
        case FrequencyType.Weekly:
        case FrequencyType.EveryTwoWeeks: {
            const { endDate, perpetual, startDate } = frequency;
            return {
                type,
                startOn: format(startDate, 'MM/dd/yyyy'),
                endOn: endDate ? format(endDate, 'MM/dd/yyyy') : undefined,
                noEndDate: perpetual || undefined,
                repeatOn: FrequencyDay[(frequency as WeeklyFrequency).repeatOn],
            } as FrequencyModelDto;
        }
        case FrequencyType.Monthly: {
            const { endDate, perpetual, startDate, repeatOn, repeatOnLastBusinessDay } =
                frequency as MonthlyFrequency;

            return {
                type,
                startOn: format(startDate, 'MM/dd/yyyy'),
                endOn: endDate ? format(endDate, 'MM/dd/yyyy') : undefined,
                noEndDate: perpetual || undefined,
                repeatOnDay1: repeatOn && !repeatOnLastBusinessDay ? repeatOn : undefined,
                repeatOnLastBusinessDay: repeatOnLastBusinessDay || undefined,
            } as FrequencyModelDto;
        }
        case FrequencyType.TwiceAMonth: {
            const { endDate, perpetual, startDate, repeatOn, repeatOnLastBusinessDay } =
                frequency as TwiceMonthlyFrequency;

            return {
                type,
                startOn: format(startDate, 'MM/dd/yyyy'),
                endOn: endDate ? format(endDate, 'MM/dd/yyyy') : undefined,
                noEndDate: perpetual || undefined,
                repeatOnDay1: repeatOn[0],
                repeatOnDay2: repeatOn[1] && !repeatOnLastBusinessDay ? repeatOn[1] : undefined,
                repeatOnLastBusinessDay: repeatOnLastBusinessDay || undefined,
            } as FrequencyModelDto;
        }
        default:
            throw new Error(`Case for FrequencyType - ${type} not handled.`);
    }
};
